import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ResumeChoiceModal from "../components/modals/ResumeChoiceModal";
import useFeatureFlags, { FEATURE_FLAGS } from "./use-feature-flags";

const ASSESSMENT_PATH = "/user/woneindex/assessment";

interface UseResumeChoiceReturn {
  /** Resumes straight away, or first asks whether to resume or start over
   *  when there are answers that starting over would discard. */
  requestResume: (hasResumableProgress: boolean, resume: () => void) => void;
  resumeChoiceModal: React.ReactNode;
}

// Asked where the learner chooses to resume, not on the assessment page, so a
// reload part-way through an attempt does not ask again.
export default function useResumeChoice(): UseResumeChoiceReturn {
  const { isEnabled } = useFeatureFlags();
  const navigate = useNavigate();
  const [pendingResume, setPendingResume] = useState<(() => void) | null>(null);

  const requestResume = (hasResumableProgress: boolean, resume: () => void) => {
    if (hasResumableProgress && isEnabled(FEATURE_FLAGS.ASSESSMENT_AUTO_SAVE)) {
      setPendingResume(() => resume);
      return;
    }
    resume();
  };

  const resumeChoiceModal = pendingResume && (
    <ResumeChoiceModal
      onResume={() => {
        setPendingResume(null);
        pendingResume();
      }}
      onStartOver={() => {
        setPendingResume(null);
        // Router state rather than a URL parameter: the assessment clears it
        // before starting, so neither a reload nor a shared link repeats it.
        navigate(ASSESSMENT_PATH, { state: { startOver: true } });
      }}
      onDismiss={() => setPendingResume(null)}
    />
  );

  return { requestResume, resumeChoiceModal };
}
